import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Sparkles, ArrowUpRight, MessageSquare } from "lucide-react";
import { FadeUp } from "@/components/Motion";
import { openChat } from "@/lib/calendly";

/**
 * Homepage promotional section for the Corpora Counsel AI chatbot.
 * Features an animated "typing" preview of a Q&A exchange to hint at the
 * experience, plus a primary CTA that opens the floating chat widget.
 */
export default function CounselCallout() {
  const { t, i18n } = useTranslation();
  const [qText, setQText] = useState("");
  const [aText, setAText] = useState("");
  const [phase, setPhase] = useState("q"); // q -> a -> pause
  const rootRef = useRef(null);
  const startedRef = useRef(false);

  const qFull = t("counsel.previewQ");
  const aFull = t("counsel.previewA");

  // Start animation once element scrolls into view
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && !startedRef.current) {
            startedRef.current = true;
            setPhase("q");
          }
        });
      },
      { threshold: 0.35 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // Reset animation when language changes (new strings)
  useEffect(() => {
    setQText("");
    setAText("");
    startedRef.current = false;
    setPhase("idle");
    const t1 = setTimeout(() => {
      startedRef.current = true;
      setPhase("q");
    }, 120);
    return () => clearTimeout(t1);
  }, [i18n.language]);

  useEffect(() => {
    if (phase !== "q") return;
    let i = 0;
    const id = setInterval(() => {
      i += 1;
      setQText(qFull.slice(0, i));
      if (i >= qFull.length) {
        clearInterval(id);
        setTimeout(() => setPhase("a"), 520);
      }
    }, 28);
    return () => clearInterval(id);
  }, [phase, qFull]);

  useEffect(() => {
    if (phase !== "a") return;
    let i = 0;
    const id = setInterval(() => {
      i += 1;
      setAText(aFull.slice(0, i));
      if (i >= aFull.length) {
        clearInterval(id);
        setPhase("done");
      }
    }, 16);
    return () => clearInterval(id);
  }, [phase, aFull]);

  return (
    <section
      ref={rootRef}
      data-testid="counsel-callout"
      className="section-pad relative overflow-hidden border-t border-[var(--line)]"
      style={{ background: "var(--ink)" }}
    >
      {/* subtle background accents */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none opacity-[0.08]"
        style={{
          background:
            "radial-gradient(600px 300px at 20% 30%, rgba(190,170,140,0.5), transparent 60%), radial-gradient(500px 280px at 80% 70%, rgba(190,170,140,0.35), transparent 60%)",
        }}
      />
      <div className="max-w-[1480px] mx-auto px-6 md:px-10 relative grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-center">
        {/* Left: copy + CTA */}
        <div className="md:col-span-5">
          <FadeUp>
            <div className="flex items-center gap-3 mb-6">
              <span
                className="flex items-center justify-center"
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 6,
                  background: "rgba(190,170,140,0.12)",
                  border: "1px solid var(--line-2)",
                }}
              >
                <Sparkles size={13} style={{ color: "var(--pewter)" }} />
              </span>
              <div className="overline" style={{ margin: 0 }}>
                {t("counsel.eyebrow")}
              </div>
            </div>
            <h2 className="display text-5xl md:text-6xl lg:text-7xl text-[var(--bone)] leading-[1.02]">
              {t("counsel.titleA")}{" "}
              <span className="display-italic" style={{ color: "var(--pewter)" }}>
                {t("counsel.titleB")}
              </span>
            </h2>
            <p className="mt-8 text-[var(--bone-2)] leading-relaxed text-lg md:text-xl max-w-xl">
              {t("counsel.body")}
            </p>
            <ul className="mt-8 space-y-3">
              {(t("counsel.bullets", { returnObjects: true }) || []).map((b, i) => (
                <li
                  key={i}
                  className="flex items-start gap-3 text-[var(--bone-2)]"
                >
                  <span
                    aria-hidden
                    className="inline-block mt-[9px] shrink-0"
                    style={{
                      width: 18,
                      height: 1,
                      background: "var(--pewter)",
                    }}
                  />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
            <div className="mt-10 flex flex-wrap gap-4 items-center">
              <button
                type="button"
                onClick={openChat}
                data-testid="counsel-ask-btn"
                className="btn-primary inline-flex items-center gap-3"
              >
                <MessageSquare size={15} />
                {t("counsel.ctaBtn")}
              </button>
              <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--mute-2)]">
                {t("counsel.ctaHint")}
              </span>
            </div>
          </FadeUp>
        </div>

        {/* Right: animated mock conversation */}
        <FadeUp className="md:col-span-7" delay={0.15}>
          <div
            className="relative"
            style={{
              background: "rgba(14,16,20,0.85)",
              border: "1px solid var(--line-2)",
              borderRadius: 14,
              padding: "28px 28px 34px",
              boxShadow: "0 30px 90px -40px rgba(0,0,0,0.9)",
            }}
            data-testid="counsel-preview"
          >
            {/* Header row */}
            <div
              className="flex items-center justify-between mb-7 pb-5"
              style={{ borderBottom: "1px solid var(--line)" }}
            >
              <div className="flex items-center gap-3">
                <div
                  className="flex items-center justify-center"
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 8,
                    background: "rgba(190,170,140,0.14)",
                    border: "1px solid var(--line-2)",
                  }}
                >
                  <Sparkles size={15} style={{ color: "var(--pewter)" }} />
                </div>
                <div>
                  <div className="display text-base text-[var(--bone)] leading-tight">
                    {t("chat.title")}
                  </div>
                  <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-[var(--mute-2)]">
                    {t("chat.subtitle")}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 font-mono text-[9px] uppercase tracking-[0.22em] text-[var(--mute-2)]">
                <span
                  className="inline-block"
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: 999,
                    background: "#7fb08a",
                    boxShadow: "0 0 10px #7fb08a",
                  }}
                />
                {t("counsel.statusLive")}
              </div>
            </div>

            {/* User bubble */}
            <div className="flex justify-end mb-5">
              <div
                className="max-w-[85%] px-4 py-3 rounded-lg text-sm leading-relaxed"
                style={{ background: "var(--pewter)", color: "var(--ink)", minHeight: 42 }}
              >
                {qText}
                {phase === "q" && <TypingCursor />}
              </div>
            </div>

            {/* Assistant bubble */}
            <div className="flex justify-start">
              <div
                className="max-w-[92%] px-4 py-3 rounded-lg text-sm leading-relaxed whitespace-pre-wrap"
                style={{
                  background: "rgba(255,255,255,0.03)",
                  color: "var(--bone-2)",
                  border: "1px solid var(--line)",
                  minHeight: 60,
                }}
              >
                {phase === "q" || phase === "idle" ? (
                  <DotsTyping />
                ) : (
                  <>
                    {aText}
                    {phase === "a" && <TypingCursor />}
                  </>
                )}
              </div>
            </div>

            {/* CTA strip mirror */}
            <button
              type="button"
              onClick={openChat}
              data-testid="counsel-preview-open"
              className="mt-6 w-full flex items-center justify-between gap-3 px-4 py-3 group"
              style={{
                border: "1px solid var(--line)",
                borderRadius: 8,
                background: "rgba(190,170,140,0.04)",
              }}
            >
              <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--bone-2)] group-hover:text-[var(--bone)]">
                {t("counsel.openFullChat")}
              </span>
              <ArrowUpRight size={14} style={{ color: "var(--pewter)" }} />
            </button>
          </div>
        </FadeUp>
      </div>
    </section>
  );
}

function TypingCursor() {
  return (
    <span
      aria-hidden
      className="inline-block align-baseline"
      style={{
        width: 8,
        height: 14,
        marginLeft: 2,
        background: "currentColor",
        opacity: 0.75,
        animation: "counselCursorBlink 0.9s steps(1) infinite",
      }}
    />
  );
}

function DotsTyping() {
  return (
    <span className="inline-flex gap-1 align-middle" aria-label="thinking">
      <span className="counsel-dot" />
      <span className="counsel-dot" style={{ animationDelay: "0.15s" }} />
      <span className="counsel-dot" style={{ animationDelay: "0.3s" }} />
      <style>{`
        .counsel-dot {
          display: inline-block;
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: var(--pewter);
          animation: counselDot 1s infinite ease-in-out;
        }
        @keyframes counselDot {
          0%, 60%, 100% { opacity: 0.3; transform: translateY(0); }
          30% { opacity: 1; transform: translateY(-3px); }
        }
        @keyframes counselCursorBlink {
          50% { opacity: 0; }
        }
      `}</style>
    </span>
  );
}
